import { Icon } from '@progress/kendo-react-common';
import {
  Checkbox,
  CheckboxChangeEvent,
  RadioButton,
  RadioButtonChangeEvent,
} from '@progress/kendo-react-inputs';
import { FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import SearchBox from 'src/components/design-system/SearchBox';

export interface FolderOption {
  id: number;
  name: string;
}

interface AddToFavoritesDropdownProps {
  label?: string;
  folders?: FolderOption[];
  onAdd?: (folderId: number) => Promise<void>;
  onAddMultiple?: (folderIds: number[]) => Promise<void>;
  multiSelect?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  isLoading?: boolean;
  isBusy?: boolean;
  companySpecified?: boolean;
  onCompanySpecifiedAction?: (isCompanySpecified: boolean) => Promise<void>;
  size?: 'sm' | 'md' | 'lg';
}

const FAVORITES_DROPDOWN_OPEN_EVENT = 'favorites-dropdown-open';

const AddToFavoritesDropdown: FC<AddToFavoritesDropdownProps> = ({
  label,
  folders = [],
  onAdd,
  onAddMultiple,
  multiSelect = false,
  disabled = false,
  disabledMessage,
  isLoading = false,
  isBusy = false,
  companySpecified = false,
  onCompanySpecifiedAction,
  size = 'md',
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const instanceId = useId();
  const isButtonDisabled = disabled || isBusy || isLoading;

  const handleFolderSelect = useCallback((event: RadioButtonChangeEvent) => {
    const folderId = typeof event.value === 'string' ? parseInt(event.value, 10) : event.value;
    setSelectedFolder(folderId);
  }, []);

  const handleFolderToggle = useCallback((folderId: number, event: CheckboxChangeEvent) => {
    setSelectedFolders((prev) =>
      event.value ? [...prev, folderId] : prev.filter((id) => id !== folderId)
    );
  }, []);

  const hasSelection = multiSelect ? selectedFolders.length > 0 : selectedFolder !== null;

  const handleAddToFavorites = useCallback(async () => {
    if (multiSelect) {
      if (selectedFolders.length > 0 && onAddMultiple) {
        setIsDropdownOpen(false);
        setSelectedFolders([]);
        setSearchValue('');
        await onAddMultiple(selectedFolders);
      }
      return;
    }

    if (selectedFolder !== null && onAdd) {
      setIsDropdownOpen(false);
      setSelectedFolder(null);
      setSearchValue('');
      await onAdd(selectedFolder);
    }
  }, [multiSelect, onAdd, onAddMultiple, selectedFolder, selectedFolders]);

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    setSelectedFolder(null);
    setSelectedFolders([]);
    setSearchValue('');
  }, []);

  const handleButtonClick = useCallback(() => {
    if (isButtonDisabled) {
      return;
    }

    if (isDropdownOpen) {
      handleCloseDropdown();
      return;
    }

    document.dispatchEvent(new CustomEvent(FAVORITES_DROPDOWN_OPEN_EVENT, { detail: instanceId }));
    setIsDropdownOpen(true);
  }, [handleCloseDropdown, instanceId, isButtonDisabled, isDropdownOpen]);

  const handleCompanySpecifiedSelect = useCallback(
    async (isCompanySpecified: boolean) => {
      if (!onCompanySpecifiedAction) {
        return;
      }

      setIsDropdownOpen(false);
      await onCompanySpecifiedAction(isCompanySpecified);
    },
    [onCompanySpecifiedAction]
  );

  const filteredFolders = useMemo(() => {
    if (!searchValue.trim()) {
      return folders;
    }
    return folders.filter((folder: FolderOption) =>
      folder.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, folders]);

  const buttonLabel = label ?? t('Add to Shopping List');
  const disabledTooltipMessage =
    disabledMessage ?? t('Please select at least one item to Add to Shopping List');
  const folderSelectionTitle = multiSelect ? t('Select Lists') : t('Select a List');
  const folderSelectionTooltip = multiSelect
    ? t('Please select at least one list')
    : t('Please select a list');

  useEffect(() => {
    const handleOtherDropdownOpen = (event: Event) => {
      const { detail } = event as CustomEvent<string>;
      if (detail !== instanceId) {
        handleCloseDropdown();
      }
    };

    document.addEventListener(FAVORITES_DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);

    return () => {
      document.removeEventListener(FAVORITES_DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
    };
  }, [handleCloseDropdown, instanceId]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleCloseDropdown();
      }
    };

    const handleKeyboardDismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseDropdown();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      window.requestAnimationFrame(() => {
        if (!dropdownRef.current?.contains(document.activeElement)) {
          handleCloseDropdown();
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyboardDismiss);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyboardDismiss);
    };
  }, [handleCloseDropdown, isDropdownOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
        className="max-h-[180px]"
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type="Information" contents={[disabledTooltipMessage]} />
          </div>
        }
        show={disabled}
      >
        <Button
          color="secondary"
          size={size}
          onClick={handleButtonClick}
          endIcon={<Icon name={isDropdownOpen ? 'caret-alt-up' : 'caret-alt-down'} />}
          disabled={isButtonDisabled}
          loading={isLoading}
          aria-expanded={isDropdownOpen}
          aria-haspopup="dialog"
        >
          {buttonLabel}
        </Button>
      </PopUp>

      {isDropdownOpen && companySpecified ? (
        <div
          className="k-animation-container k-animation-container-shown absolute top-full inline-dropdown-overlay"
          style={{
            minWidth: '220px',
            left: 0,
          }}
        >
          <div className="k-popup k-group k-reset k-list-container overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
            <div
              className="flex items-center justify-between px-3 py-2 bg-gray-200 text-white rounded-t-md"
              style={{ height: '50px' }}
            >
              <h3 className="text-sm font-semibold text-black">{t('Make a Selection')}</h3>
              <button
                type="button"
                onClick={handleCloseDropdown}
                className="text-black hover:bg-gray-100 px-1 cursor-pointer"
                aria-label={t('Close')}
              >
                <Icon name="close" />
              </button>
            </div>
            <button
              type="button"
              className="k-list-item block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void handleCompanySpecifiedSelect(true)}
              disabled={isLoading}
            >
              {t('Mark as Company Specified')}
            </button>
            <button
              type="button"
              className="k-list-item block w-full cursor-pointer border-t border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void handleCompanySpecifiedSelect(false)}
              disabled={isLoading}
            >
              {t('Unmark as Company Specified')}
            </button>
          </div>
        </div>
      ) : null}

      {isDropdownOpen && !companySpecified ? (
        <div
          className="k-animation-container k-animation-container-shown absolute top-full inline-dropdown-overlay"
          style={{
            width: '290px',
            left: 0,
          }}
        >
          <div className="k-popup k-group k-reset k-list-container bg-white border border-gray-300 shadow-lg rounded-md">
            {/* Header */}
            <div
              className="flex items-center justify-between px-3 py-2 bg-gray-200 text-white rounded-t-md"
              style={{ height: '50px' }}
            >
              <h3 className="text-sm font-semibold text-black">{folderSelectionTitle}</h3>
              <button
                type="button"
                onClick={handleCloseDropdown}
                className="text-black hover:bg-gray-100 px-1 cursor-pointer"
                aria-label={t('Close')}
              >
                <Icon name="close" />
              </button>
            </div>

            {/* Search Box */}
            <div className="px-2 py-2" style={{ height: '56px' }}>
              <SearchBox
                placeholder={t('Search')}
                width="100%"
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>

            {/* Folder List */}
            <div
              className="overflow-auto"
              style={{
                maxHeight: '220px',
              }}
            >
              {filteredFolders.length > 0 ? (
                <div className="px-2 py-1">
                  {filteredFolders.map((folder: FolderOption) => (
                    <div key={folder.id} className="flex items-center mb-1">
                      {multiSelect ? (
                        <>
                          <Checkbox
                            checked={selectedFolders.includes(folder.id)}
                            onChange={(event) => handleFolderToggle(folder.id, event)}
                          />
                          <label
                            className="text-sm cursor-pointer ml-2"
                            onClick={() =>
                              setSelectedFolders((prev) =>
                                prev.includes(folder.id)
                                  ? prev.filter((id) => id !== folder.id)
                                  : [...prev, folder.id]
                              )
                            }
                          >
                            {folder.name}
                          </label>
                        </>
                      ) : (
                        <>
                          <RadioButton
                            value={folder.id.toString()}
                            checked={selectedFolder === folder.id}
                            onChange={handleFolderSelect}
                          />
                          <label
                            className="text-sm cursor-pointer ml-2"
                            onClick={() => setSelectedFolder(folder.id)}
                          >
                            {folder.name}
                          </label>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <Icon name="warning" className="text-2xl mb-2" />
                  <p>{t('No Shopping List Found')}</p>
                </div>
              )}
            </div>

            {/* Add Button */}
            <div className="px-2 py-2 flex justify-start rounded-b-md">
              <PopUp
                popupAlign={{ horizontal: 'left', vertical: 'top' }}
                anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
                className="max-h-[180px]"
                content={
                  <div className="m-1 max-w-[350px]">
                    <PopUpContentWrapper type="Information" contents={[folderSelectionTooltip]} />
                  </div>
                }
                show={!hasSelection}
              >
                <Button
                  color="info"
                  size="lg"
                  onClick={handleAddToFavorites}
                  disabled={!hasSelection || isLoading}
                  loading={isLoading}
                >
                  {t('Add')}
                </Button>
              </PopUp>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddToFavoritesDropdown;
